import { createHashRouter } from "react-router-dom";
import { Suspense, lazy } from "react";
import { routes } from "./routes";
import { PageSizeLoader } from "../../components/CustomComponents/PageSizeLoader";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { RequireSetupStep } from "./guards";

const FieldSetupLayout = lazy(() => import("../../layouts/FieldSetup"));

const ROUTE_GUARD_CONFIG: Record<string, { requireStep?: number }> = {
  setupDashboard: {},
  setupUsers: {},
  setupUsersPending: {},
  setupUserDetail: {},
  setupProfile: {},
  step1Account: {},
  step2Device: { requireStep: 1 },
  step3Verify: { requireStep: 2 },
  step4Handover: { requireStep: 3 },
  setupComplete: { requireStep: 4 },
  catchAll: {},
};

const SpinnerFallback = () => (
  <div
    className="flex items-center justify-center p-8"
    aria-busy="true"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px",
    }}
  >
    <div
      role="presentation"
      style={{
        width: "48px",
        height: "48px",
        border: "4px solid #161646",
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

export const setupRouter = createHashRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={<PageSizeLoader loader={<SpinnerFallback />} />}>
        <FieldSetupLayout />
      </Suspense>
    ),
    children: Object.entries(routes).map(([routeKey, route]) => ({
      ...route,
      element: (
        <AppErrorBoundary
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <ErrorComponent
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          )}
          resetKeys={[routeKey]}
        >
          <Suspense fallback={<PageSizeLoader loader={<SpinnerFallback />} />}>
            {(() => {
              const cfg = ROUTE_GUARD_CONFIG[routeKey] ?? {};
              const Element = route.element;
              if (cfg.requireStep !== undefined) {
                return (
                  <RequireSetupStep requiredStep={cfg.requireStep}>
                    <Element />
                  </RequireSetupStep>
                );
              }
              return <Element />;
            })()}
          </Suspense>
        </AppErrorBoundary>
      ),
    })),
  },
]);
